import React, { useCallback, useEffect, useState } from 'react';
import { getComments, addComment, deleteComment } from '../../lib/episodeSocial';
import { storageImage } from '../../lib/img';

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 2)  return 'just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 7)  return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function CommentsSection({
  episode,
  user,
  profile,
  onCountChange,
  toast,
}) {
  const [comments, setComments] = useState([]);
  const [body, setBody]         = useState('');
  const [busy, setBusy]         = useState(false);
  const [loading, setLoading]   = useState(true);

  const reload = useCallback(async () => {
    if (!episode?.id) { setComments([]); setLoading(false); return; }
    try {
      setLoading(true);
      const rows = await getComments(episode.id);
      setComments(rows);
      onCountChange?.(rows.length);
    } catch (err) {
      console.error('getComments failed:', err);
    } finally {
      setLoading(false);
    }
  }, [episode?.id, onCountChange]);

  useEffect(() => { reload(); }, [reload]);

  async function submit(e) {
    e?.preventDefault?.();
    if (!user?.id) { toast?.warn?.('Sign in to comment.'); return; }
    if (!body.trim() || busy) return;
    try {
      setBusy(true);
      const row = await addComment(user.id, episode.id, body);
      setComments(prev => [row, ...prev]);
      setBody('');
      onCountChange?.(comments.length + 1);
    } catch (err) {
      toast?.error?.(err?.message || 'Could not post comment.');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(c) {
    if (!user?.id || c.user_id !== user.id) return;
    try {
      await deleteComment(user.id, c.id);
      setComments(prev => prev.filter(x => x.id !== c.id));
      onCountChange?.(Math.max(0, comments.length - 1));
    } catch (err) {
      toast?.error?.(err?.message || 'Could not delete.');
    }
  }

  const initials = (profile?.full_name?.[0] || user?.email?.[0] || '?').toUpperCase();

  return (
    <section className="ws-comments">
      <div className="ws-comments-head">
        <h3 className="ws-comments-title">
          {loading ? 'Comments' : `${comments.length} Comment${comments.length === 1 ? '' : 's'}`}
        </h3>
      </div>

      <form className="ws-comment-compose" onSubmit={submit}>
        <span className="ws-comment-avatar">
          {profile?.avatar_url ? <img src={storageImage(profile.avatar_url, { width: 96 })} alt="" /> : <span>{initials}</span>}
        </span>
        <input
          type="text"
          className="ws-comment-input"
          placeholder={user ? 'Write a comment' : 'Sign in to comment'}
          value={body}
          onChange={e => setBody(e.target.value)}
          maxLength={2000}
          disabled={!user || busy}
        />
        <button
          type="submit"
          className="ws-btn-primary ws-btn-tiny"
          disabled={!user || !body.trim() || busy}
        >
          {busy ? 'Posting…' : 'Post'}
        </button>
      </form>

      {loading ? (
        <div className="ws-comments-loading">Loading comments…</div>
      ) : comments.length === 0 ? (
        <div className="ws-comments-empty">No comments yet. Kick things off.</div>
      ) : (
        <div className="ws-comments-list">
          {comments.map(c => (
            <CommentRow
              key={c.id}
              comment={c}
              isMine={c.user_id === user?.id}
              onDelete={() => handleDelete(c)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function CommentRow({ comment, isMine, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  // Minimal display — we don't have a public profile-lookup table for
  // commenters yet. Phase B wires up real names + avatars.
  return (
    <article className="ws-comment">
      <span className="ws-comment-avatar ws-comment-avatar-small">
        <span>?</span>
      </span>
      <div className="ws-comment-body">
        <div className="ws-comment-head">
          <span className="ws-comment-author">{isMine ? 'You' : 'Listener'}</span>
          <span className="ws-comment-time">{timeAgo(comment.created_at)}</span>
          {isMine && (
            <div className="ws-comment-menu">
              <button className="ws-comment-menu-btn" onClick={() => setMenuOpen(v => !v)} aria-label="Comment actions">⋯</button>
              {menuOpen && (
                <div className="ws-comment-menu-list">
                  <button className="ws-comment-menu-item danger" onClick={() => { setMenuOpen(false); onDelete?.(); }}>Delete</button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="ws-comment-text">{comment.body}</div>
      </div>
    </article>
  );
}
